import cx from 'clsx';
import { Box, CellProps, Table, TableProps, Tbody, Td, Th, Thead, Tr, vars } from '@ff-it/ui';
import { DragHandle, useDraggable, useDragMonitor } from 'components/DND';
import { ReactElement, ReactNode } from 'react';
import { useFieldArray } from 'react-final-form-arrays';
import { AddInline, InlineRenderProps, Inlines, RemoveInline } from './Inlines';
import { controlCol } from 'styles/style.css';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { FormInlineError } from './FormInlineError';
import { cellClassname } from './TableInlines.css';

// similar to DataGrid
export type TableInlineColumn<V = any> = {
  key: string;
  content?: ReactNode;
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;
  render: (props: InlineRenderProps<V>) => ReactNode;
  cellProps?: CellProps;
  headerCellProps?: CellProps;
};

type TableInlineRowProps = InlineRenderProps & {
  inlineName: string;
  min?: number | false;
  columns: TableInlineColumn[];
  stortable: boolean;
};

function TableInlineRow({ inlineName, min, columns, stortable, ...props }: TableInlineRowProps): ReactElement {
  const { index, disabled } = props;
  const { elementRef, handleRef, isDragging, closestEdge } = useDraggable(index, disabled, inlineName);

  return (
    <Tr
      style={{ position: 'relative', backgroundColor: isDragging ? vars.pallete.blue50 : undefined }}
      ref={elementRef}
    >
      {stortable && (
        <Td textAlign="center" flush>
          <DragHandle disabled={disabled} ref={handleRef} active={isDragging} />
          {closestEdge && <DropIndicator edge={closestEdge} />}
        </Td>
      )}
      {columns.map(({ key, render, cellProps: { className, ...cellProps } = {} }) => (
        <Td key={key} className={cx(cellClassname, className)} {...cellProps}>
          {render(props)}
        </Td>
      ))}
      {min !== false && (
        <Td flush textAlign="center">
          <RemoveInline name={inlineName} index={index} min={min} />
        </Td>
      )}
    </Tr>
  );
}

type TableInlinesProps<T = any> = {
  name: string;
  initialValues?: T;
  children?: InlineRenderProps;
  columns: TableInlineColumn[];
  min?: number | false;
  actions?: ReactNode;
  tfoot?: ReactNode;
  sortable?: boolean;
} & Pick<TableProps, 'width'>;

export function TableInlines<T = any>({
  name,
  columns,
  initialValues,
  min,
  actions,
  tfoot,
  sortable = true,
  ...tableProps
}: TableInlinesProps<T>): ReactElement {
  const { fields } = useFieldArray<T>(name, { subscription: {} });
  useDragMonitor((sourceData, targetData, edge) => {
    if (edge === 'bottom') {
      fields.move(sourceData.index, targetData.index + 1);
    } else if (edge === 'top') {
      fields.move(sourceData.index, targetData.index);
    }
  }, name);

  return (
    <Table
      marginBottom="md"
      size="sm"
      textSize="sm"
      before={<FormInlineError name={name} className="mb-1" />}
      fixed
      after={
        initialValues || actions ? (
          <Box border="light" padding="sm" style={{ borderTop: 0 }}>
            <Box display="flex">
              {initialValues && (
                <AddInline name={name} initialValues={initialValues}>
                  Add rate
                </AddInline>
              )}
              {actions}
            </Box>
          </Box>
        ) : null
      }
      {...tableProps}
    >
      <colgroup>
        {sortable && <col />}
        {columns.map(({ key, width, minWidth, maxWidth }) => (
          <col key={key} style={{ width, minWidth, maxWidth }} />
        ))}
        {min !== false && <col />}
      </colgroup>
      <Thead variant="dark">
        <Tr>
          {sortable && <Th className={controlCol} />}
          {columns.map(({ key, content, cellProps, headerCellProps }) => (
            <Th key={key} {...cellProps} {...headerCellProps}>
              {content}
            </Th>
          ))}
          {min !== false && <Th className={controlCol} />}
        </Tr>
      </Thead>
      <Tbody>
        <Inlines name={name}>
          {(props) => <TableInlineRow {...props} stortable={sortable} min={min} inlineName={name} columns={columns} />}
        </Inlines>
      </Tbody>
      {tfoot}
    </Table>
  );
}
